import { useEffect, useRef, useState } from 'react';

/**
 * Однократная видимость элемента во вьюпорте (IntersectionObserver).
 * Используется для ленивых запросов в длинных списках: запрос стартует, когда строка
 * показалась на экране, и больше не выключается (данные уже в кэше).
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(): {
  ref: React.RefObject<T | null>;
  inView: boolean;
} {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView || !ref.current) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      // Нет IO (старое окружение/тесты) — не ломаемся, просто грузим сразу.
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '120px' },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [inView]);

  return { ref, inView };
}
